import { Element } from '@thive/canvas';
import { Group } from '@thive/fddm-types';
import { Vector2, Vector3 } from 'three';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { areasToFlatCollection, isHighwayArea } from '@/modules/floorplanDesign/helpers';
import {
  findSegmentContainingPoint,
  templatesToFlatCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { Area } from '@/modules/floorplanDesign/types';

type ResolveClickedGroupAreaOptions = {
  eligibleAreaIds?: ReadonlySet<string>;
  prioritizeSmallestArea?: boolean;
};

const flattenGroups = (groups: Group[]): Group[] =>
  groups.flatMap((group) => [group, ...flattenGroups(group.nestedGroups)]);

const getGroupAreaIds = (group: Group): string[] => [
  ...group.areaIds,
  ...group.nestedGroups.flatMap((nestedGroup) => getGroupAreaIds(nestedGroup)),
];

const areaContainsPoint = (area: Area, point: Vector2): boolean => {
  if (isHighwayArea(area)) {
    return Boolean(findSegmentContainingPoint(area.segments, point));
  }

  const { rectangle } = area;
  const translatedX = point.x - rectangle.centerX;
  const translatedY = point.y - rectangle.centerY;
  const cos = Math.cos(rectangle.angle);
  const sin = Math.sin(rectangle.angle);
  const localX = translatedX * cos + translatedY * sin;
  const localY = -translatedX * sin + translatedY * cos;

  return Math.abs(localX) <= rectangle.length / 2 && Math.abs(localY) <= rectangle.width / 2;
};

const getAreaFootprint = (area: Area): number => {
  if (isHighwayArea(area)) return Number.POSITIVE_INFINITY;

  return area.rectangle.width * area.rectangle.length;
};

const getDistanceToAreaCenterSquared = (area: Area, point: Vector2): number => {
  if (isHighwayArea(area)) return Number.POSITIVE_INFINITY;

  const deltaX = point.x - area.rectangle.centerX;
  const deltaY = point.y - area.rectangle.centerY;

  return deltaX * deltaX + deltaY * deltaY;
};

const compareAreasByClickSpecificity =
  (point: Vector2) =>
  (areaA: Area, areaB: Area): number => {
    const footprintDifference = getAreaFootprint(areaA) - getAreaFootprint(areaB);

    if (footprintDifference !== 0) return footprintDifference;

    return (
      getDistanceToAreaCenterSquared(areaA, point) - getDistanceToAreaCenterSquared(areaB, point)
    );
  };

export const resolveClickedGroupArea = (
  element: Element,
  clickedPosition: Vector3,
  options?: ResolveClickedGroupAreaOptions,
): Area | undefined => {
  const state = useFloorplanDesignStore.getState();
  const templateGroups = flattenGroups(
    templatesToFlatCollection(state.design.templates).map((template) => template.group),
  );
  const { parentId } = element;
  const directGroup = state.getGroupById(element.id);
  const parentGroup = parentId ? state.getGroupById(parentId) : undefined;
  const templateGroup = templateGroups.find(
    (group) => group.id === element.id || group.id === parentId,
  );
  const clickedGroup = directGroup ?? parentGroup ?? templateGroup;

  if (!clickedGroup || !clickedGroup.canConnect) return undefined;

  const clickedGroupAreaIds = new Set(getGroupAreaIds(clickedGroup));
  const clickedPoint = new Vector2(clickedPosition.x, clickedPosition.y).divideScalar(1000);
  const matchingAreas = areasToFlatCollection(state.design.areas).filter((area) => {
    if (!clickedGroupAreaIds.has(area.id)) return false;
    if (options?.eligibleAreaIds && !options.eligibleAreaIds.has(area.id)) return false;
    if (!areaContainsPoint(area, clickedPoint)) return false;

    const containingGroups = [
      ...flattenGroups(state.getGroupsByAreaIds([area.id])),
      ...templateGroups.filter((group) => getGroupAreaIds(group).includes(area.id)),
    ];

    return containingGroups.every((group) => group.canConnect);
  });

  if (!options?.prioritizeSmallestArea) return matchingAreas[0];

  return matchingAreas.sort(compareAreasByClickSpecificity(clickedPoint))[0];
};
